import React, { useState, useMemo, useContext, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getCouponReports } from "api/report";
import { AppContext } from "Context/AppContext";
import styles from "./index.module.scss";
import ReactTable from "../../../Components/Table/Table";
import { setAllStore } from "Helpers/setAllStore";
import { STATUS_FILTER, COUPON_REPORT_TYPE } from "Helpers/Constants";
import { convertDateTime } from "Helpers/date";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { safeError } from "utils/logger";

const CouponReport = ({ type, appliedFilters, checkAllStore = true }) => {
  const navigate = useNavigate();
  const { showToastMessage } = useContext(AppContext);

  // ============================================================================
  // REFS - Used to prevent duplicate API calls on initial render
  // ============================================================================
  
  /** 
   * Tracks if this is the initial mount to skip duplicate useEffect call.
   * ReactTable calls fetchData on mount, so we skip the useEffect on first render.
   */
  const isInitialMount = useRef(true);

  /**
   * Stores the previous appliedFilters to detect actual changes.
   * This prevents unnecessary API calls when filters haven't changed.
   */
  const prevAppliedFiltersRef = useRef(null);

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  /** Loading state for showing spinner during API calls */
  const [loading, setLoading] = useState(false);

  /** Array of coupon report data from API */
  const [data, setData] = useState([]);

  /** Total number of pages for pagination */
  const [pageCount, setPageCount] = useState(1);

  /**
   * Column visibility state - Initialized from localStorage for persistence.
   * Uses lazy initializer to avoid reading localStorage on every render.
   */
  const [columnStateSaveData, setColumnStateSaveData] = useState(() => {  
    const cachedColumnData = localStorage.getItem(`columnStateSaveDataCouponReport_${type}`);
    if (cachedColumnData) {
      try {
        return JSON.parse(cachedColumnData);
      } catch (e) {
        return {};
      }
    }
    return {};
  });

  /** Status filter options for the status column dropdown */
  const statusList = STATUS_FILTER;

  /**
   * Default filter state for ReactTable.
   * Stores current pagination, sorting, and filter values.
   */
  const [defaultFilter, setDefaultFilter] = useState({
    pageIndex: 0,
    pageSize: 10,
    sortBy: [],
    filters: [],
    globalFilter: "",
  });

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Effect to refetch data when appliedFilters change from parent.
   * 
   * IMPORTANT: We skip the initial mount because ReactTable already calls
   * fetchData on mount. This prevents duplicate API calls.
   * 
   * We also compare with previous filters to avoid unnecessary calls
   * when the parent re-renders without actual filter changes.
   */
  useEffect(() => {
    // Skip the first render - ReactTable handles initial fetch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevAppliedFiltersRef.current = appliedFilters;
      return;
    }

    // Check if filters actually changed (using timestamp as change indicator)
    const filtersChanged = 
      prevAppliedFiltersRef.current?.timestamp !== appliedFilters?.timestamp;

    if (filtersChanged) {
      prevAppliedFiltersRef.current = appliedFilters;
      
      // Reset to page 0 and fetch with new filters
      fetchData({
        ...defaultFilter,
        pageIndex: 0,
        fromDateDefault: appliedFilters?.fromDate,
        toDateDefault: appliedFilters?.toDate
      });
    }
  }, [appliedFilters]);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  /**
   * Fetches coupon report data from the API.
   * 
   * This function is called by:
   * 1. ReactTable on initial mount and when pagination/sort changes
   * 2. useEffect when appliedFilters change from parent
   * 
   * @param {Object} params - Fetch parameters
   * @param {number} params.pageIndex - Current page (0-indexed)
   * @param {number} params.pageSize - Items per page
   * @param {Array} params.sortBy - Sort configuration
   * @param {string} params.globalFilter - Global search term
   * @param {Array} params.filters - Column filters
   * @param {string} params.fromDateDefault - Override start date
   * @param {string} params.toDateDefault - Override end date
   */
  const fetchData = useCallback(async ({
    pageIndex,
    pageSize,
    sortBy,
    globalFilter,
    filters,
    fromDateDefault,
    toDateDefault,
  }) => {
    // Update default filter state for future reference
    setDefaultFilter({
      pageSize,
      pageIndex,
      filters,
      globalFilter,
      sortBy,
    });

    // Set localStorage flag for all-store checkbox behavior
    setAllStore(checkAllStore);

    setLoading(true);

    // Build API request payload
    const postData = {
      currentPage: pageIndex,
      itemsPerPage: pageSize,
      globalSearch: globalFilter,
      filters,
      sortBy,
      start_date: fromDateDefault ?? appliedFilters?.fromDate,
      end_date: toDateDefault ?? appliedFilters?.toDate,
      coupon_type: type,
    };

    try {
      const response = await getCouponReports(postData);
      
      if (response && response.status === 200) {
        // Success - update data and page count
        setData(response.data.data.rows || []);
        setPageCount(Math.ceil((response.data.data.count || 0) / pageSize));
      } else if (response?.status === 403) {
        // Forbidden - redirect to dashboard
        showToastMessage(undefined, response.data.message, "error", false);
        navigate("/dashboard");
      } else {
        // Other error - show message
        showToastMessage(undefined, response?.data?.message || "An error occurred", "error", false);
      }
    } catch (error) {
      safeError("Error fetching coupon reports:", error);
      showToastMessage(undefined, "Failed to fetch coupon reports", "error", false);
    } finally {
      setLoading(false);
    }

    // Reset all-store flag after fetch completes
    if (checkAllStore) {
      setAllStore(false);
    }
  }, [type, appliedFilters, checkAllStore, navigate, showToastMessage]);

  // ============================================================================
  // TABLE COLUMNS CONFIGURATION
  // ============================================================================

  /**
   * Memoized column definitions for ReactTable.
   * Each column has visibility controlled by columnStateSaveData.
   */
  const columns = useMemo(() => [
    {
      Header: "Coupon Name",
      accessor: "coupon_name",
      isVisible: columnStateSaveData.coupon_name ?? true,
      placeholder: "Search By Coupon Name",
      colWidth: "220px",
      Cell: ({ row }) => row.original.coupon_name ?? "-",
    },
    {
      Header: "Coupon Description",
      accessor: "coupon_description",
      isVisible: columnStateSaveData.coupon_description ?? false,
      disableFilters: true,
      Cell: ({ row }) => {
        const description = row.original?.coupon_description;
        
        // Handle null, undefined, or empty string
        if (!description || description.trim() === "") {
          return "-";
        }
        
        // If description is less than 75 characters, show it directly
        if (description.length < 75) {
          return description;
        }
        
        // If description is 75 or more characters, show truncated with tooltip
        const tooltipId = `description_${type}_${row.original?.id}`;
        return (
          <>
            <div data-tooltip-id={tooltipId}>
              {description.slice(0, 75) + "..."}
            </div>
            <ReactTooltip
              id={tooltipId}
              effect="solid"
              place="top"
              arrowColor="#000"
              aria-haspopup="true"
              className={styles.descriptionTooltip}
              delayHide={1000}
              delayShow={500}
              delayUpdate={500}
            >
              <p className="py-1">{description}</p>
            </ReactTooltip>
          </>
        );
      },
    },
    {
      Header: "Type (% or Fixed)",
      accessor: "coupon_type",
      isVisible: columnStateSaveData.coupon_type ?? true,
      disableFilters: true,
      Cell: ({ row }) => row.original.coupon_type === "Percentage" ? "%" : "Fixed",
    },
    {
      Header: "Value",
      accessor: "coupon_value",
      isVisible: columnStateSaveData.coupon_value ?? true,
      disableFilters: true,
      Cell: ({ row }) => row.original.coupon_value ?? "-",
    },
    {
      Header: "Start date",
      accessor: "active_start_date",
      isVisible: columnStateSaveData.active_start_date ?? true,
      disableFilters: true,
      Cell: ({ row }) => (
        <div>
          {row.original.active_start_date
            ? convertDateTime(row.original.active_start_date, "seconds", "MM/dd/yyyy")
            : "-"}
        </div>
      ),
    },
    {
      Header: "End date",
      accessor: "active_end_date",
      isVisible: columnStateSaveData.active_end_date ?? true,
      disableFilters: true,
      Cell: ({ row }) => (
        <div>
          {row.original.active_end_date
            ? convertDateTime(row.original.active_end_date, "seconds", "MM/dd/yyyy")
            : "-"}
        </div>
      ),
    },
    {
      Header: "Status",
      accessor: "coupon_status",
      isVisible: columnStateSaveData.coupon_status ?? true,
      isSelectFilter: true,
      placeholder: "status",
      selectFilterOption: statusList,
      Cell: ({ row }) => (
        <div className={styles.status}>
          {row.original.status === 1 ? (
            <div className={styles.Active}>Active</div>
          ) : (
            <div className={styles.Inactive}>Inactive</div>
          )}
        </div>
      ),
    },
    {
      Header: "Redemptions",
      accessor: "coupon_redeemed",
      isVisible: columnStateSaveData.coupon_redeemed ?? true,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.coupon_redeemed ?? 0}
        </div>
      ),
    },
    {
      Header: "AOV Before Coupon",
      accessor: "average_order_value_before_coupon",
      isVisible: columnStateSaveData.average_order_value_before_coupon ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.average_order_value_before_coupon ?? "-"}
        </div>
      ),
    },
    {
      Header: "AOV After Coupon",
      accessor: "average_order_value_after_coupon",
      isVisible: columnStateSaveData.average_order_value_after_coupon ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.average_order_value_after_coupon ?? "-"}
        </div>
      ),
    },
    {
      Header: "Average Redeemed Value",
      accessor: "average_coupon_size_redeemed",
      isVisible: columnStateSaveData.average_coupon_size_redeemed ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.average_coupon_size_redeemed ?? "-"}
        </div>
      ),
    },
    {
      Header: "Total Dollars Redeemed",
      accessor: "total_redeemed_coupon_value",
      isVisible: columnStateSaveData.total_redeemed_coupon_value ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.total_redeemed_coupon_value > 0 
            ? row.original.total_redeemed_coupon_value 
            : 0}
        </div>
      ),
    },
    {
      Header: "Customer Lifetime Value",
      accessor: "average_lifetime_revenue",
      isVisible: columnStateSaveData.average_lifetime_revenue ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.average_lifetime_revenue ?? "-"}
        </div>
      ),
    },
    {
      Header: "Apply to (product or total)",
      accessor: "coupon_apply_to",
      isVisible: columnStateSaveData.coupon_apply_to ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.coupon_apply_to === "Specific Product" ? "Product" : "Total"}
        </div>
      ),
    },
    {
      Header: "Only Mobile App (y/n)",
      accessor: "coupon_redeem_for_mobile",
      isVisible: columnStateSaveData.coupon_redeem_for_mobile ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.coupon_redeem_for_mobile ? "Y" : "N"}
        </div>
      ),
    },
    {
      Header: "Limit to 1 use (y/n)",
      accessor: "usage_limit_one_per_user",
      isVisible: columnStateSaveData.usage_limit_one_per_user ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.usage_limit_one_per_user ? "Y" : "N"}
        </div>
      ),
    },
    {
      Header: "Max usage",
      accessor: "usage_limit_number_count",
      isVisible: columnStateSaveData.usage_limit_number_count ?? false,
      disableFilters: true,
      Cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          {row.original.usage_limit_number_count > 0 
            ? row.original.usage_limit_number_count 
            : "N/A"}
        </div>
      ),
    },
    {
      // Hidden column for row identification
      Header: "",
      accessor: "id",
      colWidth: "1px",
      isVisible: true,
      Cell: () => null,
    },
  ], [columnStateSaveData, type, statusList]);

  // ============================================================================
  // COLUMN VISIBILITY PERSISTENCE
  // ============================================================================

  /**
   * Callback to persist column visibility state to localStorage.
   * Called by ReactTable when user toggles column visibility.
   */
  const selectedColumn = useCallback((column) => {
    if (column) {
      setColumnStateSaveData((prevState) => {
        // Compare with previous state to prevent infinite updates
        const prevStateStr = JSON.stringify(prevState);
        const newStateStr = JSON.stringify(column);
        
        if (prevStateStr !== newStateStr) {
          const storageKey = `columnStateSaveDataCouponReport_${type}`;
          localStorage.setItem(storageKey, newStateStr);
          return column;
        }
        return prevState;
      });
    }
  }, [type]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <React.Fragment>
      <div id="MainContent" className="main-content">
        <div style={{ width: "100%" }}>
          <ReactTable
            columns={columns}
            data={data}
            fetchData={fetchData}
            loading={loading}
            pageCount={pageCount}
            noDataText="There is no data to display"
            defaultFilter={defaultFilter}
            displayGlobalSearch={false}
            displayColumnVisibility={true}
            selectedColumn={selectedColumn}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default CouponReport;
